<template>
    <span>
        <card-grid-item
            :tab-widget="tabWidget"
            :card-class="['card-grid']"
            :min-w="3"
            :min-h="minHeight"
            :resized="handleResized"
            @tab-widget-settings-click="handleTabWidgetSettingsClick"
        >
            <template v-slot:card-title-prepend>
                <b-dropdown variant="link" no-caret toggle-class="p-0 mr-2">
                    <template #button-content>
                        <font-awesome-icon class="text-secondary" :icon="['fas', 'ellipsis-vertical']" />
                    </template>
                    <b-dropdown-item :key="filter.filter" v-for="filter in filters" @click="setFilter(filter.filter)">
                        {{ filter.title }}
                    </b-dropdown-item>
                </b-dropdown>
            </template>
            <template v-slot:card-title>
                {{ title }}
            </template>
            <template v-slot:card-body>
                <busy-modal :show="busy" class="chart-widget-spinner" />
                <card-grid
                    ref="grid"
                    :key="cardGridKey"
                    :header-height="50"
                    :is-search="false"
                    :columnDefs="columnDefs"
                    :rowData="rowData"
                    :pagination="true"
                    :pagination-page-size="limit"
                    :cache-block-size="limit"
                    :is-auto-width="true"
                    :font-size="tabWidget.settings.table_font_size"
                ></card-grid>
            </template>
            <template v-slot:tab-widget-settings-modal>
                <tab-widget-settings-modal-form
                    :modal-id="tabWidgetSettingsModalID"
                    :update="updateTabWidgetSettings"
                    :initial-values="tabWidgetSettingsInitialValues"
                    @success-update="handleSuccessTabWidgetSettingsUpdate"
                    :withTableFontSize="true"
                ></tab-widget-settings-modal-form>
            </template>
        </card-grid-item>
        <modal
            :id="changeCurrentRowFailureStatusModalId"
            :title="failureStatusConfirmationModalTitle"
            @hidden="cancelCurrentRowFailureStatusChange"
            @ok="changeCurrentRowFailureStatus"
            ok-title="Confirm"
        >
            <busy-modal :show="busy"></busy-modal>
            <p>{{ failureStatusConfirmationModalText }}</p>
        </modal>
    </span>
</template>

<script>
    import { mapGetters } from 'vuex';
    import API from '../../../../api';
    import CardGrid from '../../../generic/card-grid/card-grid.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import CardGridItem from '../common/card-grid-item.vue';
    import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
    import ShowErrorsCellRenderer from '../../../reports/failed-transactions/show-errors-cell-renderer.vue';
    import {
        REPORT_FAILED_TRANSACTIONS_ASSIGNEE_ENDPOINT,
        REPORT_FAILED_TRANSACTIONS_ASSIGNEE_OPTIONS_ENDPOINT,
        REPORT_FAILED_TRANSACTIONS_ENDPOINT,
        REPORT_FAILED_TRANSACTIONS_UNRESOLVED_ENDPOINT,
    } from '../../../../endpoints';
    import {
        DATE_PERIOD_MAPPING,
        DATE_PERIOD_YESTERDAY,
        DATE_PERIOD_TODAY,
        DATE_PERIOD_LAST_WEEK,
        DATE_PERIOD_LAST_MONTH,
        DATE_PERIOD_LAST_YEAR,
        DATE_PERIOD_THIS_WEEK,
        DATE_PERIOD_THIS_MONTH,
        DATE_PERIOD_THIS_YEAR,
    } from '../../../../constants';
    import { isMobileScreen } from '../../../../utils/screens';
    import { formatResponseError } from '../../../../utils/api';
    import { getWidgetGridRowsNumber } from '../helpers';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

    export default {
        name: 'FailedTransactionsTableCardGridItem',
        mixins: [cardGridItemBaseMixin],

        components: {
            CardGrid,
            Modal,
            BusyModal,
            CardGridItem,
            TabWidgetSettingsModalForm,
            showErrorsCellRenderer: ShowErrorsCellRenderer,
        },
        data() {
            return {
                busy: false,
                rowData: [],
                gridApi: null,
                paymentMethods: {},
                currentFilter: DATE_PERIOD_THIS_YEAR,
                previousRowFailureStatus: null,
                currentRowFailureStatus: null,
                changeFailureStatusClicked: false,
                changeCurrentRowFailureStatusModalId: 'change-current-row-failure-status-modal-id',
                assigneeOptions: [{ value: null, text: 'Not Selected' }],
                limit: getWidgetGridRowsNumber({ tabWidget: this.tabWidget }),
                failureStatusCellOptions: [
                    { text: 'Resolved', value: 'resolved' },
                    { text: 'Unresolved', value: 'unresolved' },
                    { text: 'Undergoing Resolution', value: 'undergoing_resolution' },
                ],
            };
        },
        mounted() {
            Promise.all([this.fetchAssigneeOptions(), this.fetchData()]);
            const grid = this.$refs.grid.$refs.cardGridGrid;
            this.gridApi = grid.gridOptions.api;
        },
        computed: {
            ...mapGetters('header', ['userPermissions']),
            columnDefs() {
                return [
                    {
                        field: 'date',
                        headerName: 'Date',
                        minWidth: 100,
                        sortIndex: 1,
                        sort: 'desc',
                    },
                    {
                        field: 'guest_name',
                        headerName: 'Guest Name',
                        minWidth: 100,
                    },
                    {
                        field: 'invoice_id',
                        headerName: 'Invoice #',
                        minWidth: 100,
                        cellRenderer: ({ data, node }) => {
                            if (node.rowPinned) return '';
                            return `
                            <a
                                target="_blank"
                                href="/app/guest/${data.guest_id}/invoices/?reservation=${data.invoice_id}&tab=transactions"
                            >
                                ${data.invoice_id}
                            </a>`;
                        },
                    },
                    {
                        field: 'property',
                        headerName: 'Property',
                        minWidth: 110,
                    },
                    ...Object.keys(this.paymentMethods).map((pm_key) => {
                        const pm = this.paymentMethods[pm_key];
                        return {
                            field: pm_key,
                            headerName: pm,
                            type: 'amountColumn',
                            cellStyle: ({ data, node }) => {
                                const value = data[pm_key] || 0;
                                if (value < 0) return { color: 'red' };
                                return null;
                            },
                            minWidth: 124,
                        };
                    }),
                    {
                        field: 'total',
                        headerName: 'Total',
                        type: 'amountColumn',
                        cellStyle: ({ data }) => {
                            const { total } = data;
                            if (total < 0) return { color: 'red' };
                            return null;
                        },
                        minWidth: 100,
                        hide: Object.keys(this.paymentMethods).length < 2,
                    },
                    {
                        field: 'processing_type',
                        headerName: 'Processing Type',
                        minWidth: 110,
                    },
                    {
                        field: 'sequence',
                        headerName: 'Sequence',
                        minWidth: 100,
                    },
                    {
                        field: 'transaction_code',
                        headerName: 'Transaction ID',
                        minWidth: 120,
                    },
                    {
                        field: 'is_payment_requested',
                        headerName: 'Payment Requested (Automatic)',
                        type: 'yesNoColumnNew',
                        cellRendererParams: {
                            valueGetter: ({ node, data }) => (data && !node.rowPinned ? data.is_payment_requested : null),
                        },
                        minWidth: 120,
                        maxWidth: 120,
                    },
                    {
                        field: '',
                        headerName: 'Errors',
                        minWidth: 100,
                        cellRenderer: 'showErrorsCellRenderer',
                    },
                    {
                        field: 'resolution_assignee',
                        headerName: 'Assigned for Resolution',
                        cellEditor: 'selectCellEditor',
                        cellClass: ({ node }) => (!node.rowPinned ? 'lx-editable-cell text-primary' : ''),
                        editable: ({ node }) => !node.rowPinned,
                        autoWidthExclude: true,
                        resizable: false,
                        minWidth: 300,
                        cellRenderer: ({ node, data }) =>
                            data && !node.rowPinned ? this.getAssigneeName(data.resolution_assignee) : '',
                        cellEditorParams: () => ({ options: this.assigneeOptions }),
                        valueSetter: async ({ node, data, newValue, oldValue }) => {
                            (async () => {
                                try {
                                    await API.get(REPORT_FAILED_TRANSACTIONS_ASSIGNEE_ENDPOINT(data.id), {
                                        params: { resolution_assignee: newValue },
                                    });
                                    this.refresh();
                                } catch (error) {
                                    const { generic, fields } = formatResponseError(error);
                                    const errorText =
                                        fields['resolution_assignee'] && !!fields['resolution_assignee'].length
                                            ? fields['resolution_assignee'][0]
                                            : generic;
                                    this.errorToast({ text: errorText || `Could not change assignee.` });
                                    data.resolution_assignee = oldValue;
                                    node.setData(data);
                                    return;
                                }
                            })();
                        },
                    },
                    {
                        field: 'failure_status',
                        headerName: 'Failure Status',
                        cellClass: ({ node }) => (!node.rowPinned ? 'lx-editable-cell text-primary' : ''),
                        editable: ({ node }) => !node.rowPinned,
                        cellEditor: 'selectCellEditor',
                        minWidth: 200,
                        sortIndex: 0,
                        sort: 'desc',
                        pinned: isMobileScreen() ? undefined : 'right',
                        cellRenderer: ({ data }) => data && this.getFailureStatusText(data.failure_status),
                        cellEditorParams: () => ({
                            onChange: (params, value) => {
                                this.currentRowData = params.data;
                                this.previousRowFailureStatus = this.currentRowData.failure_status;
                                this.currentRowFailureStatus = value;
                                this.$bvModal.show(this.changeCurrentRowFailureStatusModalId);
                            },
                            options: this.failureStatusCellOptions,
                        }),
                    },
                ];
            },
            failureStatusConfirmationModalTitle() {
                return `Mark Transaction as ${
                    this.currentRowFailureStatus == 'resolved'
                        ? 'Resolved'
                        : this.currentRowFailureStatus == 'unresolved'
                        ? 'Unresolved'
                        : 'Undergoing Resolution'
                }`;
            },
            failureStatusConfirmationModalText() {
                return `Please confirm this billing issue ${
                    this.currentRowFailureStatus == 'resolved'
                        ? "has been resolved."
                        : this.currentRowFailureStatus == 'unresolved'
                        ? 'is unresolved.'
                        : 'is undergoing resolution.'
                }`;
            },
            filters() {
                return [
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_YESTERDAY],
                        filter: DATE_PERIOD_YESTERDAY,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_TODAY],
                        filter: DATE_PERIOD_TODAY,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_THIS_WEEK],
                        filter: DATE_PERIOD_THIS_WEEK,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_LAST_WEEK],
                        filter: DATE_PERIOD_LAST_WEEK,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_THIS_MONTH],
                        filter: DATE_PERIOD_THIS_MONTH,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_LAST_MONTH],
                        filter: DATE_PERIOD_LAST_MONTH,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_THIS_YEAR],
                        filter: DATE_PERIOD_THIS_YEAR,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_LAST_YEAR],
                        filter: DATE_PERIOD_LAST_YEAR,
                    },
                ];
            },
            title() {
                const currentTitle = this.filters.find((item) => item.filter === this.currentFilter);
                return (this.tabWidget.settings.name || this.defaultName) + ' - ' + currentTitle.title;
            },
            tabWidgetSettingsInitialValues() {
                return {
                    settings: {
                        name: this.tabWidget.settings.name || this.defaultName,
                        table_font_size: this.tabWidget.settings.table_font_size || this.defaultWidgetFontSize,
                    },
                };
            },
        },
        methods: {
            refresh() {
                this.fetchData();
                this.cardGridKey++;
            },
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(REPORT_FAILED_TRANSACTIONS_ENDPOINT('row-data'), {
                        params: { period: this.currentFilter },
                    });
                    const { transactions, total, payment_table, payment_methods } = data;
                    this.paymentMethods = payment_methods;
                    this.rowData = [
                        ...transactions.map((transaction, index) => ({
                            id: index,
                            ...transaction,
                            ...transaction.payment_table,
                        })),
                    ];
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data.' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchAssigneeOptions() {
                try {
                    const { data } = await API.get(REPORT_FAILED_TRANSACTIONS_ASSIGNEE_OPTIONS_ENDPOINT());
                    this.assigneeOptions = [{ value: null, text: 'Not Selected' }].concat(
                        data.map((a) => {
                            return { value: a.id, text: `${a.first_name} ${a.last_name} (${a.email})` };
                        })
                    );
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch assignee options.' });
                }
            },
            cancelCurrentRowFailureStatusChange() {
                if (!this.changeFailureStatusClicked) {
                    this.currentRowData.failure_status = this.previousRowFailureStatus;
                    this.gridApi.refreshCells();
                    this.changeFailureStatusClicked = false;
                }
            },
            async changeCurrentRowFailureStatus() {
                this.changeFailureStatusClicked = true;
                try {
                    await API.get(REPORT_FAILED_TRANSACTIONS_UNRESOLVED_ENDPOINT(this.currentRowData.id), {
                        params: { failure_status: this.currentRowFailureStatus },
                    });
                    this.refresh();
                } catch (error) {
                    this.changeFailureStatusClicked = false;
                    this.cancelCurrentRowFailureStatusChange();
                    const { generic, fields } = formatResponseError(error);
                    const errorText =
                        fields['failure_status'] && !!fields['failure_status'].length
                            ? fields['failure_status'][0]
                            : generic;
                    this.errorToast({
                        text:
                            errorText ||
                            `Could not change failure status to ${this.getFailureStatusText(
                                this.currentRowFailureStatus
                            )}.`,
                    });
                }
            },
            getFailureStatusText(failureStatus) {
                switch (failureStatus) {
                    case 'resolved':
                        return 'Resolved';
                    case 'unresolved':
                        return 'Unresolved';
                    case 'undergoing_resolution':
                        return 'Undergoing Resolution';
                }
            },
            getAssigneeName(assigneeId) {
                return this.assigneeOptions.find((a) => a.value == assigneeId)?.text || 'Not Selected';
            },
            setFilter(filter) {
                this.currentFilter = filter;
                this.fetchData();
                this.handleResized();
            },
            handleResized() {
                this.limit = getWidgetGridRowsNumber({ tabWidget: this.tabWidget });
                this.cardGridKey++;
            },
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.show-errors-cell-renderer .btn) {
        padding: 0.12rem 0.25rem !important;
        font-size: 0.775rem !important;
    }
</style>
